import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calculator, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';

interface CreditSimulatorProps {
  propertyPrice: number;
}

const CreditSimulator: React.FC<CreditSimulatorProps> = ({ propertyPrice }) => {
  const { t } = useTranslation();
  const [months, setMonths] = useState<string>('');
  const [interestRate, setInterestRate] = useState<string>('');
  const [ivaRate, setIvaRate] = useState<string>('10');
  const [showResults, setShowResults] = useState(false);

  const calculation = useMemo(() => {
    const monthsNum = parseInt(months);
    const annualRate = parseFloat(interestRate);
    const iva = parseFloat(ivaRate);

    if (!monthsNum || !annualRate || isNaN(iva) || monthsNum <= 0 || annualRate <= 0) {
      return null;
    }

    // Calculate total with IVA
    const totalWithIva = propertyPrice * (1 + iva / 100);

    // Monthly interest rate
    const monthlyRate = annualRate / 100 / 12;

    // Monthly payment formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
    const monthlyPayment = totalWithIva * (monthlyRate * Math.pow(1 + monthlyRate, monthsNum)) / (Math.pow(1 + monthlyRate, monthsNum) - 1);

    // Total payment over the loan term
    const totalPayment = monthlyPayment * monthsNum;

    // Total interest paid
    const totalInterest = totalPayment - totalWithIva;

    return {
      monthlyPayment,
      totalPayment,
      totalInterest,
      totalWithIva,
      ivaAmount: propertyPrice * (iva / 100)
    };
  }, [months, interestRate, ivaRate, propertyPrice]);

  const handleCalculate = () => {
    if (calculation) {
      setShowResults(true);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const isFormValid = months && interestRate && ivaRate && parseInt(months) > 0 && parseFloat(interestRate) > 0;

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-green-100 p-3 rounded-lg">
          <Calculator className="h-6 w-6 text-green-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('creditSimulator.title')}</h2>
          <p className="text-sm text-gray-500">{t('creditSimulator.subtitle')}</p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Property Price Display */}
        <div className="bg-gray-50 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-600 mb-1">
            {t('creditSimulator.propertyPrice')}
          </label>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(propertyPrice)}</div>
        </div>

        {/* Input Fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('creditSimulator.loanTerm')}
            </label>
            <input
              type="number"
              value={months}
              onChange={(e) => {
                setMonths(e.target.value);
                setShowResults(false);
              }}
              placeholder={t('creditSimulator.monthsPlaceholder')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              min="1"
              max="360"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('creditSimulator.interestRate')}
            </label>
            <div className="relative">
              <input
                type="number"
                value={interestRate}
                onChange={(e) => {
                  setInterestRate(e.target.value);
                  setShowResults(false);
                }}
                placeholder={t('creditSimulator.ratePlaceholder')}
                className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                min="0.1"
                max="100"
                step="0.1"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('creditSimulator.ivaRate')}
            </label>
            <div className="relative">
              <input
                type="number"
                value={ivaRate}
                onChange={(e) => {
                  setIvaRate(e.target.value);
                  setShowResults(false);
                }}
                placeholder="10"
                className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                min="0"
                max="100"
                step="0.5"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
            </div>
          </div>
        </div>

        <Button
          onClick={handleCalculate}
          disabled={!isFormValid}
          className="w-full bg-green-600 hover:bg-green-700 text-white py-3"
        >
          {t('creditSimulator.calculate')}
        </Button>

        {/* Results */}
        {showResults && calculation && (
          <div className="mt-6 space-y-4">
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <label className="block text-sm font-medium text-green-700 mb-1">
                {t('creditSimulator.monthlyPayment')}
              </label>
              <div className="text-3xl font-bold text-green-700">
                {formatCurrency(calculation.monthlyPayment)}
              </div>
              <p className="text-sm text-green-600 mt-1">
                {t('creditSimulator.perMonth')}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  {t('creditSimulator.ivaAmount')}
                </label>
                <div className="text-lg font-semibold text-gray-900">
                  {formatCurrency(calculation.ivaAmount)}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  {t('creditSimulator.totalWithIva')}
                </label>
                <div className="text-lg font-semibold text-gray-900">
                  {formatCurrency(calculation.totalWithIva)}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  {t('creditSimulator.totalInterest')}
                </label>
                <div className="text-lg font-semibold text-gray-900">
                  {formatCurrency(calculation.totalInterest)}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  {t('creditSimulator.totalPayment')}
                </label>
                <div className="text-lg font-semibold text-gray-900">
                  {formatCurrency(calculation.totalPayment)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                {t('creditSimulator.disclaimerTitle')}
              </p>
              <p className="text-sm text-amber-700 mt-1">
                {t('creditSimulator.disclaimerText')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditSimulator;
